import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import { getCurrentUser } from "../lib/database";
import debounce from "lodash.debounce";
import { LoadingProvider } from "./LoadingContext";
import { Platform } from "../types";
import {
  Draft,
  getDrafts,
  saveDraftPost as saveDraftToStorage,
  updateDraft as updateDraftInStorage,
  deleteDraft as deleteDraftFromStorage,
  useDraftStore,
} from "../utils/draftStorage";
import Pusher from "pusher-js";
import API, { PublicAPI } from "../services/api";
import Cookies from "js-cookie";
import { oauthManagerClient } from "@/lib/oauthManagerClient";
import { useUser } from "@/store/useUser";
import { useLocation, useNavigate } from "react-router-dom";
import { useDraftStoreZustand } from "@/store/useDraftStore";
import { pusher } from "@/lib/pusher";

export interface User {
  id: string;
  email: string;
  name?: string;
  profile_type?: "business" | "individual";
  plan?: "free" | "ipro" | "business";
  created_at?: string;
  user_metadata?: { name?: string; [key: string]: any };
  wallet: any;
  profile: Profile | null;
}

export interface Profile {
  id: string;
  name: string;
  type: "individual" | "business";
  industry: string;
  description?: string;
  tone?: string;
  target_audience?: string;
  userId: string;
  plan: "free" | "ipro" | "business";
  campaignName?: string;
  campaignType?: string;
  campaignGoals?: string[];
  profession?: string;
  location?: string;
  website?: string;
  bio?: string;
  contentNiche?: string;
  preferredPlatforms?: string[];
  brandVoice?: string;
  socialGoals?: string[];
  businessGoals?: string[];
  postingFrequency?: string;
  businessName?: string;
  jobTitle?: string;
  campaignSize?: string;
  teamCollaboration?: boolean;
  customIntegrations?: string[];
  monthlyBudget?: string;
  contentVolume?: string;
  campaign_type?: string;
  content_niche?: string;
  business_name?: string;
  brandLogo?: string;
  publicUrl?: string;
}

export interface AppState {
  user: User | null;

  userPlan: "free" | "ipro" | "business" | null;
  selectedProfile: Profile | null;
  selectedCampaign: null;
  loading: boolean;
  error: string | null;
  generatedPosts: any[];
  contentData: any;
  currentDraftId: string | null;
  drafts: Draft[];
  hasCompletedOnboarding: boolean;
  isBusinessAccount: boolean;
  hasTierSelected: boolean;
  hasProfileSetup: boolean;
  balance: number;
  isProfileEditing?: boolean;
  isPasswordEditing?: boolean;
  connectedPlatforms: Platform[] | null;
  connectingPlatforms: Platform[];
  analyticsList: any[];
  analyticsLoading: boolean;
  draftHistory: any[];
  draftHistoryLoading: boolean;
}

type AppAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_USER"; payload: User | null }
  | { type: "SET_USER_PLAN"; payload: "free" | "ipro" | "business" | null }
  | { type: "SET_SELECTED_PROFILE"; payload: Profile | null }
  | { type: "SET_SELECTED_CAMPAIGN"; payload: null }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_GENERATED_POSTS"; payload: any[] }
  | {
      type: "UPDATE_SINGLE_PLATFORM_POST";
      payload: { platform: Platform; post: any };
    }
  | { type: "SET_CONTENT_DATA"; payload: any }
  | { type: "SET_ONBOARDING_COMPLETE"; payload: boolean }
  | { type: "SET_TIER_SELECTED"; payload: boolean }
  | { type: "SET_PROFILE_SETUP"; payload: boolean }
  | { type: "SET_PROFILE_EDITING"; payload: boolean }
  | { type: "SET_PASSWORD_EDITING"; payload: boolean }
  | { type: "RESET_STATE" }
  | { type: "SET_BUSINESS_ACCOUNT"; payload: boolean }
  | { type: "SET_BALANCE"; payload: number }
  | { type: "SET_PACKAGES"; payload: any[] }
  | { type: "SET_ADDONS"; payload: any[] }
  | { type: "SET_LOADER"; payload: boolean }
  | { type: "SET_UNREAD_COUNT"; payload: number }
  | { type: "SET_SECURITY_QUESTIONS"; payload: any[] }
  | { type: "SET_CONNECTED_PLATFORMS"; payload: Platform[] }
  | { type: "SET_CONNECTING_PLATFORMS"; payload: Platform[] }
  | { type: "SET_ANALYTICS"; payload: any[] }
  | { type: "SET_ANALYTICS_LOADING"; payload: boolean }
  | { type: "SET_POST_HISTORY"; payload: any[] }
  | { type: "SET_POST_HISTORY_LOADING"; payload: boolean }
  | { type: "SET_SCHEDULED_HISTORY"; payload: any[] }
  | { type: "SET_DRAFT_HISTORY"; payload: any }
  | { type: "SET_EXCHANGE_RATES"; payload: Record<string, number> }
  | { type: "SET_EXCHANGE_RATES_LOADING"; payload: boolean }
  | { type: "SET_DRAFTS"; payload: Draft[] }
  | { type: "ADD_DRAFT"; payload: Draft }
  | { type: "UPDATE_DRAFT"; payload: Draft }
  | { type: "DELETE_DRAFT"; payload: string }
  | { type: "SET_CURRENT_DRAFT_ID"; payload: string | null }
  | { type: "SET_PUBLISH_MODAL"; payload: boolean }
  | { type: "SET_DRAFT_HISTORY_LOADING"; payload: boolean };

const initialState: AppState & {
  security_question: any[];
  packages: any[];
  addons: any[];
  loader: boolean;
  unreadCount: number;
  postHistory: any[];
  scheduledHistory: any[];
  draftHistory: any;
  postHistoryLoading: Boolean;
  draftHistoryLoading: Boolean;
  exchangeRatesLoading: Boolean;
  exchangeRates: any;
  showPublishModal: boolean;
} = {
  user: null,
  userPlan: null,
  selectedProfile: null,
  selectedCampaign: null,
  loading: true,
  error: null,
  generatedPosts: [],
  contentData: null,
  currentDraftId: null,
  drafts: [],
  hasCompletedOnboarding: false,
  isBusinessAccount: false,
  hasTierSelected: false,
  hasProfileSetup: false,
  balance: 0,
  packages: [],
  security_question: [],
  addons: [],
  loader: true,
  unreadCount: 0,
  connectedPlatforms: null,
  connectingPlatforms: [],
  analyticsList: [],
  analyticsLoading: false,
  postHistory: [],
  scheduledHistory: [],
  draftHistory: [],
  postHistoryLoading: true,
  draftHistoryLoading: true,
  exchangeRates: {},
  exchangeRatesLoading: false,
  showPublishModal: false,
};

function appReducer(
  state: typeof initialState,
  action: AppAction
): typeof initialState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_BALANCE":
      return { ...state, balance: action.payload };
    case "SET_USER":
      return { ...state, user: action.payload };
    case "SET_USER_PLAN":
      return { ...state, userPlan: action.payload };
    case "SET_SELECTED_PROFILE":
      return {
        ...state,
        selectedProfile: action.payload,
        userPlan: action.payload?.plan || state.userPlan,
        isBusinessAccount: action.payload?.type === "business",
      };
    case "SET_SELECTED_CAMPAIGN":
      return { ...state, selectedCampaign: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_GENERATED_POSTS":
      return { ...state, generatedPosts: action.payload };
    case "UPDATE_SINGLE_PLATFORM_POST":
      const { platform, post } = action.payload;
      return {
        ...state,
        generatedPosts: state.generatedPosts.map((p) =>
          p.platform === platform ? post : p
        ),
      };
    case "SET_CONTENT_DATA":
      return { ...state, contentData: action.payload };
    case "SET_ONBOARDING_COMPLETE":
      return { ...state, hasCompletedOnboarding: action.payload };
    case "SET_TIER_SELECTED":
      return { ...state, hasTierSelected: action.payload };
    case "SET_PROFILE_SETUP":
      return { ...state, hasProfileSetup: action.payload };
    case "SET_PROFILE_EDITING":
      return { ...state, isProfileEditing: action.payload };
    case "SET_PASSWORD_EDITING":
      return { ...state, isPasswordEditing: action.payload };
    case "RESET_STATE":
      return { ...initialState, loading: false };
    case "SET_BUSINESS_ACCOUNT":
      return { ...state, isBusinessAccount: action.payload };
    case "SET_PACKAGES":
      return { ...state, packages: action.payload };
    case "SET_SECURITY_QUESTIONS":
      return { ...state, security_question: action.payload };
    case "SET_ADDONS":
      return { ...state, addons: action.payload };
    case "SET_LOADER":
      return { ...state, loader: action.payload };
    case "SET_UNREAD_COUNT":
      return { ...state, unreadCount: action.payload };
    case "SET_CONNECTED_PLATFORMS":
      return { ...state, connectedPlatforms: action.payload };

    case "SET_CONNECTING_PLATFORMS":
      return { ...state, connectingPlatforms: action.payload };
    case "SET_ANALYTICS_LOADING":
      return { ...state, analyticsLoading: action.payload };
    case "SET_DRAFT_HISTORY_LOADING":
      return { ...state, draftHistoryLoading: action.payload };
    case "SET_ANALYTICS":
      return { ...state, analyticsList: action.payload };
    case "SET_POST_HISTORY":
      return { ...state, postHistory: action.payload };
    case "SET_SCHEDULED_HISTORY":
      return { ...state, scheduledHistory: action.payload };

    case "SET_DRAFT_HISTORY":
      return { ...state, draftHistory: action.payload };

    case "SET_POST_HISTORY_LOADING":
      return { ...state, postHistoryLoading: action.payload };
    case "SET_EXCHANGE_RATES":
      return { ...state, exchangeRates: action.payload };

    case "SET_EXCHANGE_RATES_LOADING":
      return { ...state, exchangeRatesLoading: action.payload };

    case "SET_DRAFTS":
      return { ...state, drafts: action.payload };

    case "ADD_DRAFT":
      return { ...state, drafts: [action.payload, ...state.drafts] };

    case "UPDATE_DRAFT":
      return {
        ...state,
        drafts: state.drafts.map((d) =>
          d.id === action.payload.id ? action.payload : d
        ),
      };

    case "DELETE_DRAFT":
      return {
        ...state,
        drafts: state.drafts.filter((d) => d.id !== action.payload),
      };

    case "SET_CURRENT_DRAFT_ID":
      return { ...state, currentDraftId: action.payload };

    case "SET_PUBLISH_MODAL":
      return { ...state, showPublishModal: action.payload };

    default:
      return state;
  }
}
const ALL_PLATFORMS: Platform[] = [
  "linkedin",
  "facebook",
  "instagram",
  "youtube",
  "tiktok",
];

interface AppContextType {
  state: typeof initialState;
  dispatch: React.Dispatch<AppAction>;
  processing: boolean;
  setProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  generationAmounts: any;
  setGenerationAmounts: React.Dispatch<React.SetStateAction<any>>;
  fetchBalance: () => Promise<void>;
  fetchPostHistory: (
    status: string,

    page: number,
    append: boolean
  ) => Promise<void>;
  initUser: () => Promise<void>;
  refreshUser: () => Promise<void>;
  selectCampaign: (campaign: null) => void;
  logout: () => void;
  setProfileEditing: (v: boolean) => void;
  setPasswordEditing: (v: boolean) => void;
  fetchUnreadCount: () => Promise<void>;
  refreshBalance: () => Promise<void>;
  checkConnectedPlatforms: () => Promise<void>;
  fetchAnalytics: (refresh: boolean) => Promise<void>;
  linkedinPages: any[];
  facebookPages: any[];
  youtubeChannels: any[];

  selectedLinkedinPage: string;
  selectedFacebookPage: string;
  selectedYoutubeChannel: string;

  setSelectedLinkedinPage: (id: string) => void;
  setSelectedFacebookPage: (id: string) => void;
  setSelectedYoutubeChannel: (id: string) => void;

  loadSchedulelHistory: () => Promise<void>;
  loadDraftsHistory: (page: number, append: boolean) => Promise<void>;
  fetchFacebookPages: () => Promise<void>;
  fetchLinkedPages: () => Promise<void>;
  fetchYouTubeChannels: () => Promise<void>;
  saveDraft: (posts: any[], contentData: any, title?: string) => Draft;
  updateDraft: (
    id: string,
    posts: any[],
    contentData?: any,
    title?: string
  ) => Draft | null;
  deleteDraft: (id: string) => boolean;
  loadDrafts: () => void;
  saveDraftPost: (post: any, publishResult: any) => Promise<void>;
  scheduleLoading: boolean;
  postHistory: any;
}

const AppContext = createContext<AppContextType | null>(null);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [processing, setProcessing] = useState(false);
  const [generationAmounts, setGenerationAmounts] = useState<any>({});
  const { setUser, user } = useUser();

  const [linkedinPages, setLinkedinPages] = useState<any[]>([]);
  const [facebookPages, setFacebookPages] = useState<any[]>([]);
  const [youtubeChannels, setYoutubeChannels] = useState<any[]>([]);
  const [selectedLinkedinPage, setSelectedLinkedinPage] = useState<string>("");
  const [selectedFacebookPage, setSelectedFacebookPage] = useState<string>("");
  const [selectedYoutubeChannel, setSelectedYoutubeChannel] =
    useState<string>("");

  const fetchYouTubeChannels = async () => {
    if (!state.connectedPlatforms?.includes("youtube")) return;
    try {
      const channelsResponse = await API.youtubePages();

      const channelsData = (await channelsResponse.data) || [];
      setYoutubeChannels(channelsData.channels || []);

      const savedYoutubeChannel = localStorage.getItem(
        "selectedYoutubeChannel"
      );
      if (
        savedYoutubeChannel &&
        channelsData.channels?.some((c: any) => c.id === savedYoutubeChannel)
      ) {
        setSelectedYoutubeChannel(savedYoutubeChannel);
      } else if (channelsData.channels && channelsData.channels.length > 0) {
        setSelectedYoutubeChannel(channelsData.channels[0].id);
        localStorage.setItem(
          "selectedYoutubeChannel",
          channelsData.channels[0].id
        );
      }
    } catch (error) {
      console.error("Failed to fetch YouTube channels:", error);
    }
  };

  const fetchLinkedPages = async () => {
    if (!state.connectedPlatforms?.includes("linkedin")) return;

    try {
      const res = await API.linkedinPages();
      const pagesData = res?.data?.data || [];
      setLinkedinPages(pagesData);

      const savedLinkedInPage = localStorage.getItem("selectedlinkedinPage");

      if (savedLinkedInPage === "" || !savedLinkedInPage) {
        // 🔥 If empty → post on personal account
        setSelectedLinkedinPage(""); // empty string represents personal account
        localStorage.setItem("selectedlinkedinPage", "");
      } else if (
        savedLinkedInPage &&
        pagesData.some((p: any) => p.urn === savedLinkedInPage)
      ) {
        setSelectedLinkedinPage(savedLinkedInPage);
      } else if (pagesData?.[0]?.urn) {
        setSelectedLinkedinPage(pagesData[0].urn);
        localStorage.setItem("selectedlinkedinPage", pagesData[0].urn);
      }
    } catch (error) {
      console.error("Failed to fetch LinkedIn pages:", error);
    }
  };

  const fetchFacebookPages = async () => {
    if (!state.connectedPlatforms?.includes("facebook")) return;
    try {
      const pagesResponse = await API.facebookPages();

      let pagesData = [];

      if (pagesResponse?.data?.data) {
        pagesData = Array.isArray(pagesResponse.data.data)
          ? pagesResponse.data.data
          : [];
      } else if (pagesResponse?.pages) {
        pagesData = Array.isArray(pagesResponse.pages)
          ? pagesResponse.pages
          : [];
      } else if (pagesResponse?.data?.pages) {
        pagesData = Array.isArray(pagesResponse.data.pages)
          ? pagesResponse.data.pages
          : [];
      }

      setFacebookPages(pagesData);

      const savedFacebookPage = localStorage.getItem("selectedFacebookPage");
      if (
        savedFacebookPage &&
        pagesData.some((p: any) => p.id === savedFacebookPage)
      ) {
        setSelectedFacebookPage(savedFacebookPage);
      } else if (pagesData && pagesData.length > 0) {
        setSelectedFacebookPage(pagesData[0].id);

        localStorage.setItem("selectedFacebookPage", pagesData[0].id);
      }
    } catch (error) {
    } finally {
    }
  };

  useEffect(() => {
    const facebookPage = localStorage.getItem("selectedFacebookPage");
    const linkedinPage = localStorage.getItem("selectedlinkedinPage");
    const youtubeChannel = localStorage.getItem("selectedYoutubeChannel");

    if (facebookPage) setSelectedFacebookPage(facebookPage);
    if (linkedinPage) setSelectedLinkedinPage(linkedinPage);
    if (youtubeChannel) setSelectedYoutubeChannel(youtubeChannel);
  }, [
    localStorage.getItem("selectedFacebookPage"),
    localStorage.getItem("selectedlinkedinPage"),
    localStorage.getItem("selectedYoutubeChannel"),
  ]);

  useEffect(() => {
    Promise.all([
      fetchFacebookPages(),
      fetchLinkedPages(),
      fetchYouTubeChannels(),
    ]).finally(() => {});
  }, [user?.id, state?.connectedPlatforms]);

  const apiKey = "80f18a670f8f17b074ee56f9";
  const navigate = useNavigate();

  const fetchExchangeRates = async () => {
    const url = `https://v6.exchangerate-api.com/v6/${apiKey}/latest/USD`;

    try {
      dispatch({ type: "SET_EXCHANGE_RATES_LOADING", payload: true });

      const res = await fetch(url);
      const data = await res.json();

      if (data?.result === "success") {
        dispatch({
          type: "SET_EXCHANGE_RATES",
          payload: data.conversion_rates || {},
        });
      } else {
        dispatch({ type: "SET_EXCHANGE_RATES", payload: {} });
      }
    } catch (err) {
      console.error("Failed to fetch exchange rates:", err);
      dispatch({ type: "SET_EXCHANGE_RATES", payload: {} });
    } finally {
      dispatch({ type: "SET_EXCHANGE_RATES_LOADING", payload: false });
    }
  };

  const logout = () => {
    Cookies.remove("auth_token");
    Cookies.remove("refresh_token");
    localStorage.removeItem("pusherTransportTLS");
    localStorage.removeItem("forgot_token");
    localStorage.removeItem("forgot_token_time");
    localStorage.removeItem("user-storage");
    localStorage.removeItem("oauth_state");
    localStorage.removeItem("image-template-editor.history.v1");
    localStorage.removeItem("profile_form_data");
    localStorage.removeItem("selectedFacebookPage");
    localStorage.removeItem("selectedYoutubeChannel");
    localStorage.removeItem("selectedlinkedinPage");

    setUser(null);
    dispatch({ type: "RESET_STATE" });
    dispatch({ type: "SET_LOADER", payload: false });
    loadSecurityQuestion();
  };

  const refreshToken = async () => {
    const refToken = Cookies.get("refresh_token");
    if (!refToken) return;
    try {
      const res = await PublicAPI.refreshToken(refToken);
      Cookies.set("auth_token", res.data.data.accessToken, {
        expires: 5,
        secure: true,
        sameSite: "strict",
      });
      Cookies.set("refresh_token", res.data.data.refreshToken, {
        expires: 14,
        secure: true,
        sameSite: "strict",
      });
    } catch (error: any) {
      logout();
      navigate("/auth", { replace: true });
    }
  };

  useEffect(() => {
    refreshToken();
  }, []);

  const fetchBalance = useCallback(async () => {
    try {
      const [userRes] = await Promise.all([getCurrentUser()]);
      const user = (userRes as any)?.user;
      if (user) {
        dispatch({ type: "SET_USER", payload: user });
        setUser(user);
        setProcessing(false);
      }
    } catch (error) {
      setProcessing(false);
      console.error("Balance fetch failed:", error);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res: any = await getCurrentUser();
      if (res?.user) {
        dispatch({ type: "SET_USER", payload: res.user });
        setUser(res.user);
      }
    } catch (error) {
      console.error("User refresh failed:", error);
    } finally {
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await API.unreadHistory();
      dispatch({
        type: "SET_UNREAD_COUNT",
        payload: Number(res?.data?.data?.unreadCount) || 0,
      });
    } catch (error) {
      console.error("Unread count failed:", error);
    }
  }, []);
  const location = useLocation();
  const checkProfileCompletion = (profile: Profile | null): boolean => {
    if (!profile?.plan) return false;
    const basic = !!(profile.name && profile.plan);
    if (profile.plan === "free") return basic;
    if (profile.plan === "ipro")
      return basic && !!(profile.campaignType || profile.contentNiche);
    if (profile.plan === "business") {
      return (
        basic &&
        !!(profile.businessName || profile.industry) &&
        !!(profile.campaignType || profile.contentNiche)
      );
    }
    return basic;
  };

  const initUser = async () => {
    dispatch({ type: "SET_LOADER", payload: true });
    const token = Cookies.get("auth_token");
    if (!token) {
      dispatch({ type: "SET_LOADER", payload: false });
      return;
    }

    try {
      const res: any = await getCurrentUser();
      const user = res?.user;

      dispatch({ type: "SET_USER", payload: user });
      setUser(user);

      dispatch({ type: "SET_LOADER", payload: false });

      const profile = user.profile as Profile | null;
      if (profile) {
        dispatch({ type: "SET_SELECTED_PROFILE", payload: profile });
        dispatch({
          type: "SET_USER_PLAN",
          payload: profile.plan || "free",
        });
        dispatch({
          type: "SET_BUSINESS_ACCOUNT",
          payload: profile.type === "business",
        });
        const complete = checkProfileCompletion(profile);
        dispatch({ type: "SET_TIER_SELECTED", payload: true });
        dispatch({ type: "SET_PROFILE_SETUP", payload: complete });
        dispatch({ type: "SET_ONBOARDING_COMPLETE", payload: complete });
      }
    } catch (error) {
      dispatch({ type: "SET_LOADER", payload: false });
    }
  };

  const saveDraftHelper = useCallback(
    (posts: any[], contentData: any, title?: string) => {
      const draft = saveDraftToStorage(posts, contentData, title);
      dispatch({ type: "ADD_DRAFT", payload: draft });
      dispatch({ type: "SET_CURRENT_DRAFT_ID", payload: draft.id });
      return draft;
    },
    []
  );

  const updateDraftHelper = useCallback(
    (id: string, posts: any[], contentData?: any, title?: string) => {
      const draft = updateDraftInStorage(id, posts, contentData, title);
      if (draft) {
        dispatch({ type: "UPDATE_DRAFT", payload: draft });
      }
      return draft;
    },
    []
  );

  const deleteDraftHelper = useCallback((id: string) => {
    const success = deleteDraftFromStorage(id);
    if (success) {
      dispatch({ type: "DELETE_DRAFT", payload: id });
    }
    return success;
  }, []);

  const { saveOrUpdateDraft } = useDraftStoreZustand();
  const saveDraftPost = async (
    posts: any[],
    contentData: any,
    title?: string
  ): Promise<void> => {
    try {
      console.log("Starting to save draft post:", {
        posts,
        contentData,
        title,
      });

      // Check if post already has an ID (from existing draft or regeneration)
      const existingPostId = posts[0]?.postId || posts[0]?.id;
      const existingDraftId = posts[0]?.draftId;

      let draft;

      // If there's an existing draft ID, update it instead of creating a new one
      if (existingDraftId) {
        console.log("Updating existing draft:", existingDraftId);
        draft = useDraftStore
          .getState()
          .updateDraft(existingDraftId, posts, contentData, title);
        if (!draft) {
          // Fallback to creating new draft if update fails, but preserve the draftId
          draft = useDraftStore
            .getState()
            .saveDraftPost(posts, contentData, title, existingDraftId);
        }
      } else {
        // Save as new draft with optional draftId if provided
        draft = useDraftStore
          .getState()
          .saveDraftPost(posts, contentData, title, existingDraftId);
      }

      console.log("Saved to local store:", draft);

      // Also save to backend API - use existing postId or create new one
      const postId =
        existingPostId ||
        `${posts.map((p) => p.platform)}_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;

      const payloadData: any = {
        postId,
        status: "draft",
        postContent: posts[0]?.content || posts[0]?.caption || "",
        youtubeTitle: posts[0]?.youtubeTitle || "",
        youtubeDescription: posts[0]?.youtubeDescription || "",
        tiktok_title: posts[0]?.tiktok_title || "",
        tiktok_description: posts[0]?.tiktok_description || "",
        // Save hashtag (backend expects `hashtag`) and generation prompt so drafts persist these values
        hashtag:
          posts[0]?.hashtags.toString() ||
          contentData?.hashtags.toString() ||
          [],
        prompt:
          posts[0]?.generationPrompt ||
          posts[0]?.generation_prompt ||
          contentData?.prompt ||
          "",
        publishedUrls: {},
        platforms: posts.map((p) => p.platform),
        category: "Draft",
        imageUrl: posts[0]?.imageUrl || posts[0]?.mediaUrl,
      };

      const response = await API.savePublishedUrls(payloadData);
      saveOrUpdateDraft(payloadData?.platforms[0], payloadData);

      await response.data.data;
    } catch (error: any) {
      throw error;
    }
  };

  useLayoutEffect(() => {
    initUser();
  }, []);

  useEffect(() => {
    const loadExtras = async () => {
      try {
        const [pkgRes, addRes] = await Promise.all([
          API.listPackages(),
          API.listAddons(),
        ]);
        dispatch({ type: "SET_PACKAGES", payload: pkgRes.data.data || [] });
        dispatch({ type: "SET_ADDONS", payload: addRes.data.data || [] });
      } catch (error) {
        console.error("Load packages/addons failed:", error);
      } finally {
      }
    };

    if (user?.id) {
      loadExtras();
    }
  }, [user?.id]);

  const loadSecurityQuestion = async () => {
    try {
      const question = await API.securityQuestion();
      dispatch({
        type: "SET_SECURITY_QUESTIONS",
        payload: question.data?.data || [],
      });
    } catch (error) {}
  };

  useEffect(() => {
    loadSecurityQuestion();
  }, [user?.id]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    const userChannel = pusher.subscribe(`user-${userId}`);
    userChannel.bind("subscription-success", fetchBalance);

    const walletChannel = pusher.subscribe(`wallet-${userId}`);
    walletChannel.bind("coins-update", () => {
      fetchBalance();
    });

    return () => {
      pusher.unsubscribe(`user-${userId}`);
      pusher.unsubscribe(`wallet-${userId}`);
    };
  }, [user?.id, pusher]);

  useEffect(() => {
    const loadGenAmounts = async () => {
      try {
        const res = await API.getGenerateAmount();
        const formatted = (res.data.data || []).reduce(
          (acc: any, item: any) => {
            acc[item.type] = item.amount;
            return acc;
          },
          {}
        );
        setGenerationAmounts(formatted);
      } catch (err) {
        console.error("Gen amounts failed:", err);
      }
    };
    loadGenAmounts();
  }, []);

  const checkConnectedPlatforms = useCallback(async () => {
    try {
      const response = await API.connectionsStatus();
      const statusData = response.data;

      const connected: Platform[] = [];

      ALL_PLATFORMS.forEach((platform) => {
        const status = statusData[platform];
        if (status?.connected && !status.expired) {
          connected.push(platform);
        }
      });

      dispatch({ type: "SET_CONNECTED_PLATFORMS", payload: connected });
    } catch (error) {
      dispatch({ type: "SET_CONNECTED_PLATFORMS", payload: [] });
    }
  }, []);

  const [scheduleLoading, setScheduledLoading] = useState(false);
  const loadSchedulelHistory = async () => {
    setScheduledLoading(true);
    const res = await API.getHistory("scheduled");
    const apiData = res?.data?.data || [];
    dispatch({
      type: "SET_SCHEDULED_HISTORY",
      payload: apiData || [],
    });
    setScheduledLoading(false);
  };
  useEffect(() => {
    loadSchedulelHistory();

    const userId = user?.id;
    if (!userId) return;

    const channel = pusher.subscribe(`user-${userId}`);

    channel.bind("post-status-update", () => {
      loadSchedulelHistory();
      fetchUnreadCount();
    });

    return () => {
      channel.unbind_all();
      pusher.unsubscribe(`user-${userId}`);
    };
  }, [user?.id]);

  const fetchAnalytics = async (refresh: Boolean) => {
    try {
      dispatch({ type: "SET_ANALYTICS_LOADING", payload: true });
      let data = {
        linkedinPages: selectedLinkedinPage,
        facebookPages: selectedFacebookPage,
        youtubeChannels: selectedYoutubeChannel,
      };
      console.log("data", data);
      const res = await API.facebookAnalytics(refresh, data);
      const rawData = res?.data?.data || [];

      // Transform the data to remove the extra 'data' nesting
      const transformedData = rawData.map((item: any) => {
        // The actual analytics data is inside item.data
        const analyticsData = item.data || item;

        return {
          meta: res?.data?.meta,
          platform: item.platform,
          page: analyticsData.page || {},
          summary: analyticsData.summary || {},
          performance: analyticsData.performance || {},
          top_posts: analyticsData.top_posts || { posts: [] },
          insights: analyticsData.insights || [],
        };
      });
      localStorage.setItem("refreshFirst", "true");
      dispatch({
        type: "SET_ANALYTICS",
        payload: transformedData,
      });
      dispatch({ type: "SET_ANALYTICS_LOADING", payload: false });
    } catch (e) {
      dispatch({ type: "SET_ANALYTICS", payload: [] });
      dispatch({ type: "SET_ANALYTICS_LOADING", payload: false });
    } finally {
    }
  };

  useEffect(() => {
    if (user?.id) {
      checkConnectedPlatforms();
      const refresh =
        localStorage.getItem("refreshFirst") === "true" ? false : true;
      fetchAnalytics(refresh);
      fetchExchangeRates();
    }
  }, [user?.id]);

  const loadDraftsHistory: any = useCallback(
    async (page = 1, append = false) => {
      if (loadDraftsHistory.isLoading) {
        console.log("Skipping concurrent draft fetch");
        return;
      }

      try {
        loadDraftsHistory.isLoading = true;
        dispatch({ type: "SET_DRAFT_HISTORY_LOADING", payload: true });

        const res = await API.getHistory("draft", page);

        const responseData = res?.data?.data?.data || res?.data?.data || [];
        const pagination = res?.data?.pagination ||
          res?.data?.data?.pagination || {
            page: 1,
            limit: 10,
            totalPages: 1,
            totalPosts: 0,
            hasMore: false,
          };

        const raw = Array.isArray(responseData) ? responseData : [];

        const mappedDrafts = raw.map((post: any) => {
          // Media URL resolution
          const resolvedMediaUrl =
            post.mediaUrl ||
            post.image ||
            post.imageUrl ||
            post.image_url ||
            post.media_url ||
            post?.metadata?.image ||
            post?.metadata?.image_url ||
            (Array.isArray(post.mediaAssets) && post.mediaAssets[0]?.url) ||
            (Array.isArray(post.mediaAssets) &&
            typeof post.mediaAssets[0] === "string"
              ? post.mediaAssets[0]
              : undefined);

          // Hashtags resolution
          let hashtags: string[] = [];
          if (Array.isArray(post.hashtags)) hashtags = post.hashtags;
          else if (Array.isArray(post.hashtag)) hashtags = post.hashtag;
          else if (typeof post.hashtag === "string")
            hashtags = post.hashtag.split(/[,\s]+/).filter(Boolean);
          else if (Array.isArray(post.tags)) hashtags = post.tags;
          else if (post?.metadata?.hashtags) {
            hashtags = Array.isArray(post.metadata.hashtags)
              ? post.metadata.hashtags
              : post.metadata.hashtags.split(/[,\s]+/).filter(Boolean);
          }

          return {
            postId: post.postId,
            id: post.id,
            postHistoryId: post.id,
            versionId: post.versionId || post.id,
            content:
              post.metadata?.description ||
              post.content ||
              post.originalContent ||
              "",
            platform: post.platform || post.platforms?.[0] || "unknown",
            mediaAssets: post.mediaAssets || [],
            mediaUrl: resolvedMediaUrl,
            imageUrl: post.imageUrl || post.image_url || resolvedMediaUrl,
            thumbnailUrl: post.metadata?.thumbnailUrl || "",
            hashtags,
            generationPrompt:
              post.generationPrompt ||
              post.generation_prompt ||
              post.prompt ||
              "",
            posts: post.posts || post.generatedPosts,
            updatedAt: post.updatedAt,
            createdAt: post.createdAt,
            title:
              post.metadata?.title ||
              post.originalContent?.substring(0, 80) ||
              post.content?.substring(0, 80) ||
              "Draft",
          };
        });

        dispatch({
          type: "SET_DRAFT_HISTORY",
          payload: {
            data: append
              ? [...(state.draftHistory?.data || []), ...mappedDrafts]
              : mappedDrafts,
            pagination: {
              ...pagination,
              hasMore: pagination.page < pagination.totalPages,
            },
          },
        });

        return {
          data: mappedDrafts,
          pagination,
        };
      } catch (err) {
        console.error("Error loading drafts history:", err);
        dispatch({
          type: "SET_DRAFT_HISTORY",
          payload: {
            data: [],
            pagination: { page: 1, limit: 10, totalPages: 1, hasMore: false },
          },
        });
      } finally {
        loadDraftsHistory.isLoading = false;
        dispatch({ type: "SET_DRAFT_HISTORY_LOADING", payload: false });
      }
    },
    [state.draftHistory?.data]
  ); // Add dependency

  useEffect(() => {
    if (user?.id) {
      loadDraftsHistory(1, false);
    }
  }, [user?.id]);

  const fetchPostHistory: any = useCallback(
    async (status = "published", page = 1, append = false) => {
      // Prevent concurrent requests
      if (fetchPostHistory.isLoading) {
        console.log("Skipping concurrent post fetch");
        return;
      }

      try {
        fetchPostHistory.isLoading = true;
        dispatch({ type: "SET_POST_HISTORY_LOADING", payload: true });

        const res = await API.getHistory(status, page);
        if (res?.data?.success) {
          const newData = res.data.data || [];
          const pagination = res.data.pagination;
          setPostHistory((prev) => ({
            data: append ? [...prev.data, ...newData] : newData,
            pagination: {
              ...pagination,
              hasMore: pagination.page < pagination.totalPages,
            },
          }));
        }
      } catch (error) {
        console.error("Error fetching post history:", error);
      } finally {
        fetchPostHistory.isLoading = false;
        dispatch({ type: "SET_POST_HISTORY_LOADING", payload: false });
      }
    },
    []
  );

  const [postHistory, setPostHistory] = useState({
    data: [],
    pagination: {
      page: 1,
      limit: 10,
      totalPages: 1,
      hasMore: false,
    },
  });

  useEffect(() => {
    if (user?.id) {
      fetchPostHistory("published", 1, false);
    }
  }, [user?.id, fetchPostHistory]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId || !pusher) return;

    const channel = pusher.subscribe(`user-${userId}`);
    let refreshTimeout: ReturnType<typeof setTimeout>;

    const refreshHistory = () => {
      clearTimeout(refreshTimeout);

      refreshTimeout = setTimeout(() => {
        if (!fetchPostHistory.isLoading) {
          fetchPostHistory("published", 1, false);
        }
      }, 500);
    };

    channel.bind("post-status-update", refreshHistory);

    return () => {
      clearTimeout(refreshTimeout);
      channel.unbind_all();
      pusher.unsubscribe(`user-${userId}`);
    };
  }, [user?.id, fetchPostHistory]);

  useEffect(() => {
    return () => {
      loadDraftsHistory.isLoading = false;
      fetchPostHistory.isLoading = false;
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      processing,
      setProcessing,
      generationAmounts,
      setGenerationAmounts,
      checkConnectedPlatforms,
      fetchBalance,
      refreshUser,
      scheduleLoading,
      selectCampaign: (campaign: null) =>
        dispatch({ type: "SET_SELECTED_CAMPAIGN", payload: campaign }),
      logout,
      setProfileEditing: (v: boolean) =>
        dispatch({ type: "SET_PROFILE_EDITING", payload: v }),
      setPasswordEditing: (v: boolean) =>
        dispatch({ type: "SET_PASSWORD_EDITING", payload: v }),
      fetchUnreadCount,
      refreshBalance: fetchBalance,
      fetchAnalytics,
      fetchPostHistory,
      initUser,
      loadSchedulelHistory,
      linkedinPages,
      facebookPages,
      youtubeChannels,
      loadDraftsHistory,
      selectedLinkedinPage,
      selectedFacebookPage,
      selectedYoutubeChannel,
      fetchFacebookPages,
      fetchLinkedPages,
      fetchYouTubeChannels,
      setSelectedLinkedinPage,
      setSelectedFacebookPage,
      setSelectedYoutubeChannel,
      saveDraft: saveDraftHelper,
      updateDraft: updateDraftHelper,
      deleteDraft: deleteDraftHelper,
      saveDraftPost,
      postHistory,
    }),
    [
      state,
      dispatch,
      processing,
      setProcessing,
      generationAmounts,
      setGenerationAmounts,
      fetchBalance,
      refreshUser,
      logout,
      fetchUnreadCount,
      saveDraftPost,
      scheduleLoading,
    ]
  );

  return (
    <LoadingProvider>
      <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
    </LoadingProvider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppProvider");
  }
  const [cost, setCost] = useState(0);

  const {
    state,
    dispatch,
    processing,
    setProcessing,
    generationAmounts,
    fetchBalance,
    refreshUser,
    logout,
    fetchUnreadCount,
    selectCampaign,
    setProfileEditing,
    setPasswordEditing,
    checkConnectedPlatforms,
    fetchAnalytics,
    fetchPostHistory,
    initUser,
    linkedinPages,
    facebookPages,
    youtubeChannels,
    selectedLinkedinPage,
    selectedFacebookPage,
    selectedYoutubeChannel,
    setSelectedLinkedinPage,
    setSelectedFacebookPage,
    setSelectedYoutubeChannel,
    loadSchedulelHistory,
    fetchFacebookPages,
    fetchLinkedPages,
    fetchYouTubeChannels,
    loadDraftsHistory,
    saveDraftPost,
    scheduleLoading,
    postHistory,
  } = context;

  const setUnreadCount = useCallback(
    (count: number) => {
      dispatch({ type: "SET_UNREAD_COUNT", payload: count });
    },
    [dispatch]
  );

  // Add this useEffect in your main App component
  useEffect(() => {
    const handleOAuthReturn = () => {
      // Check if we're returning from OAuth redirect (for Safari)
      const oauthInProgress = sessionStorage.getItem("oauth_in_progress");
      const platform = sessionStorage.getItem("oauth_platform") as Platform;
      const timestamp = parseInt(
        sessionStorage.getItem("oauth_timestamp") || "0"
      );
      const redirectUrl = sessionStorage.getItem("oauth_redirect_url");

      // Check if OAuth was started less than 5 minutes ago
      const isRecent = Date.now() - timestamp < 5 * 60 * 1000; // 5 minutes

      if (oauthInProgress === "true" && platform && redirectUrl && isRecent) {
        console.log(`Detected return from ${platform} OAuth flow`);

        // Clear OAuth state from storage
        sessionStorage.removeItem("oauth_in_progress");
        sessionStorage.removeItem("oauth_platform");
        sessionStorage.removeItem("oauth_redirect_url");
        sessionStorage.removeItem("oauth_timestamp");

        // If we have a stored redirect URL and we're not already there, consider redirecting
        if (window.location.href !== redirectUrl) {
          // You can optionally redirect back to the original URL
          // window.location.href = redirectUrl;
        }

        // Refresh platform connections after a short delay
        setTimeout(() => {
          if (typeof checkConnectedPlatforms === "function") {
            checkConnectedPlatforms();
          }

          // Platform-specific refreshes
          if (
            platform === "facebook" &&
            typeof fetchFacebookPages === "function"
          ) {
            fetchFacebookPages();
          } else if (
            platform === "linkedin" &&
            typeof fetchLinkedPages === "function"
          ) {
            fetchLinkedPages();
          } else if (
            platform === "youtube" &&
            typeof fetchYouTubeChannels === "function"
          ) {
            fetchYouTubeChannels();
          }
        }, 1500);
      }
    };

    // Run on component mount
    handleOAuthReturn();

    // Also listen for when the page becomes visible again (user returns from OAuth)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setTimeout(handleOAuthReturn, 500);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    checkConnectedPlatforms,
    fetchFacebookPages,
    fetchLinkedPages,
    fetchYouTubeChannels,
  ]);

  const handleConnectPlatform = useCallback(
    async (platform: Platform) => {
      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        !(window as any).MSStream;
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );

      // ============================================
      // FOR SAFARI/iOS: USE REDIRECT METHOD (100% WORKING)
      // ============================================
      if (isIOS || isSafari) {
        console.log(
          `Using redirect method for ${platform} (Safari/iOS detected)`
        );

        try {
          dispatch({
            type: "SET_CONNECTING_PLATFORMS",
            payload: [...state.connectingPlatforms, platform],
          });

          // Get OAuth URL
          const result: any = await oauthManagerClient.startOAuthFlow(platform);
          const authUrl = result?.data?.data?.authUrl;
          if (!authUrl) throw new Error("No auth URL returned from server");

          // Store OAuth state in sessionStorage (more secure than localStorage)
          sessionStorage.setItem("oauth_in_progress", "true");
          sessionStorage.setItem("oauth_platform", platform);
          sessionStorage.setItem("oauth_redirect_url", window.location.href);
          sessionStorage.setItem("oauth_timestamp", Date.now().toString());

          // Redirect to OAuth URL
          window.location.href = authUrl;
        } catch (error: any) {
          console.error("OAuth error:", error);
          dispatch({
            type: "SET_CONNECTING_PLATFORMS",
            payload: state.connectingPlatforms.filter((p) => p !== platform),
          });

          // User-friendly error
          if (error.message?.includes("blocked")) {
            alert(
              `Please allow popups for ${platform} or try the redirect method.`
            );
          } else {
            alert(`Failed to connect to ${platform}: ${error.message}`);
          }
        }
        return; // Exit early for Safari
      }

      // ============================================
      // FOR NON-SAFARI BROWSERS: USE POPUP METHOD
      // ============================================
      console.log(`Using popup method for ${platform}`);

      let authWindow: Window | null = null;
      let checkClosed: any = null;

      try {
        // Mark platform as connecting
        dispatch({
          type: "SET_CONNECTING_PLATFORMS",
          payload: [...state.connectingPlatforms, platform],
        });

        // CRITICAL: Open popup with about:blank FIRST
        authWindow = window.open(
          "about:blank",
          `${platform}_oauth_${Date.now()}`, // Unique name to avoid conflict
          "width=600,height=700,scrollbars=yes,resizable=yes,top=100,left=100"
        );

        if (!authWindow) {
          // Popup was blocked, try redirect method as fallback
          const useRedirect = window.confirm(
            `Popup was blocked. To connect ${platform}, we'll redirect you to the authorization page. Click OK to continue.`
          );
          if (useRedirect) {
            const result: any =
              await oauthManagerClient.startOAuthFlow(platform);
            const authUrl = result?.data?.data?.authUrl;
            if (authUrl) {
              sessionStorage.setItem("oauth_in_progress", "true");
              sessionStorage.setItem("oauth_platform", platform);
              sessionStorage.setItem(
                "oauth_redirect_url",
                window.location.href
              );
              sessionStorage.setItem("oauth_timestamp", Date.now().toString());
              window.location.href = authUrl;
            }
          }
          return;
        }

        // Get OAuth URL
        const result: any = await oauthManagerClient.startOAuthFlow(platform);
        const authUrl = result?.data?.data?.authUrl;
        if (!authUrl) {
          throw new Error("No auth URL returned from server");
        }

        // Navigate the popup to the auth URL
        authWindow.location.href = authUrl;

        // PostMessage listener for OAuth completion
        const messageListener = (event: MessageEvent) => {
          // Security: Validate origin
          const allowedOrigins = [
            window.location.origin,
            "https://4q2ddj89-5000.uks1.devtunnels.ms",
            "https://omnishare.ai",
            "http://localhost:3000",
            "http://localhost:5173",
          ];

          // Skip origin check for Safari (it sometimes sends null origin)
          if (
            event.origin !== "null" &&
            !allowedOrigins.includes(event.origin)
          ) {
            console.warn("Message from unauthorized origin:", event.origin);
            return;
          }

          if (!event.data?.type || event.data.provider !== platform) return;

          console.log("OAuth message received:", event.data);

          switch (event.data.type) {
            case "oauth_success":
              authWindow?.close();
              if (checkClosed) clearInterval(checkClosed);
              window.removeEventListener("message", messageListener);

              // Platform-specific fetches
              setTimeout(() => {
                checkConnectedPlatforms();
                if (platform === "facebook") {
                  fetchFacebookPages();
                } else if (platform === "linkedin") {
                  fetchLinkedPages();
                } else if (platform === "youtube") {
                  fetchYouTubeChannels();
                }
                fetchAnalytics(true);
              }, 1000);

              break;

            case "oauth_error":
            case "oauth_cancelled":
              authWindow?.close();
              if (checkClosed) clearInterval(checkClosed);
              window.removeEventListener("message", messageListener);

              setTimeout(() => {
                checkConnectedPlatforms();
              }, 500);
              break;
          }
        };

        window.addEventListener("message", messageListener);

        // Check if popup was closed manually
        checkClosed = setInterval(() => {
          if (authWindow?.closed) {
            if (checkClosed) clearInterval(checkClosed);
            window.removeEventListener("message", messageListener);

            setTimeout(() => {
              checkConnectedPlatforms();
            }, 1000);
          }
        }, 800);
      } catch (error: any) {
        console.error("OAuth error:", error);

        // Clean up
        if (checkClosed) clearInterval(checkClosed);
        authWindow?.close();

        // Show user-friendly error
        alert(`Failed to connect to ${platform}: ${error.message}`);

        setTimeout(() => {
          checkConnectedPlatforms();
        }, 500);
      } finally {
        dispatch({
          type: "SET_CONNECTING_PLATFORMS",
          payload: state.connectingPlatforms.filter((p) => p !== platform),
        });
      }
    },
    [
      state.connectingPlatforms,
      checkConnectedPlatforms,
      fetchFacebookPages,
      fetchLinkedPages,
      fetchYouTubeChannels,
    ]
  );
  const handleDisconnectPlatform = useCallback(
    async (platform: Platform) => {
      try {
        await oauthManagerClient.disconnectPlatform(platform);
        checkConnectedPlatforms();
        fetchAnalytics(true);
      } catch (error) {
        console.error("Failed to disconnect:", error);
      }
    },
    [checkConnectedPlatforms]
  );

  const [useLogo, setUseLogo] = useState(false);
  const [useTheme, setUseTheme] = useState(false);

  useEffect(() => {
    const savedLogo = localStorage.getItem("useLogo");
    const savedTheme = localStorage.getItem("useTheme");

    setUseLogo(savedLogo === "true");
    setUseTheme(savedTheme === "true");
  }, []);

  return {
    state: state,
    postHistory,
    setUseLogo,
    setUseTheme,
    linkedinPages,
    facebookPages,
    youtubeChannels,
    selectedLinkedinPage,
    selectedFacebookPage,
    selectedYoutubeChannel,
    setSelectedLinkedinPage,
    setSelectedFacebookPage,
    setSelectedYoutubeChannel,
    loadSchedulelHistory,
    loadDraftsHistory,
    useLogo,
    useTheme,
    security_question: state.security_question,
    user: state.user,
    balance: state.balance,
    profile: state.selectedProfile,
    campaign: state.selectedCampaign,
    loader: state.loader,
    addons: state.addons,
    packages: state.packages,
    unreadCount: state.unreadCount,
    setUnreadCount,
    initUser: initUser,
    dispatch: dispatch,
    fetchUnreadCount,
    paymentProcessing: processing,
    setProcessing: setProcessing,
    cost,
    setCost,
    generationAmounts: generationAmounts,
    fetchPostHistory: fetchPostHistory,
    refreshUser,
    refreshBalance: fetchBalance,
    selectCampaign,
    logout,
    setProfileEditing,
    setPasswordEditing,
    fetchAnalytics,
    connectedPlatforms: state.connectedPlatforms,
    connectingPlatforms: state.connectingPlatforms,
    checkConnectedPlatforms: checkConnectedPlatforms,
    handleConnectPlatform: handleConnectPlatform,
    handleDisconnectPlatform: handleDisconnectPlatform,
    saveDraftPost,
    scheduleLoading,
  };
};
